<template>
    <div class="bg-white nav-margin">
        <navigation></navigation>
        <nonPremiumJobScreen v-if="!isPremiumAccount" />
        <div v-if="isPremiumAccount">
            <div
                class="back-btn bg-white border-bottom border-gray-100 d-flex gap-12 justify-content-between flex-wrap gap-2 py-3 px-3">
                <a class="p-0 d-flex align-items-center gap-1 fs-14" href="/jobs">
                    <img src="/img/chevron-left-gray.svg" alt="chevron-down" />
                    Back
                </a>
            </div>
            <main v-if="!isLoading" class="bg-white flex-fill overflow-auto">
                <div class="job-details-container mx-auto py-32">
                    <div class="mb-10">
                        <h6 class="text-gray-900 fw-semibold mb-2 fs-18">Job Status</h6>
                        <p class="job-status-subheading fw-normal pb-3 text-gray-500 fs-12">Define and customize
                            statuses to track the progress of
                            your jobs.</p>
                    </div>

                    <div class="d-flex flex-column job-status-carts">
                        <div v-for="mainStatus in mainStatuses" :key="mainStatus.id"
                            class="job-status-item bg-ashore-white">
                            <div
                                class="job-status-heading d-flex align-items-center justify-content-between gap-12 p-12 border-bottom border-gray-100">
                                <div class="d-flex align-items-center gap-2">
                                    <div class="icon">
                                        <img :src="getMainStatusIcon(mainStatus.id)" width="16" alt="status-icon">
                                    </div>
                                    <div class="fs-14 font-weight-bold text-gray-900">{{ mainStatus.name }}</div>
                                </div>
                                <button class="btn p-0" @click="addNewStatus(mainStatus.id)">
                                    <img src="/img/plus-light.svg" width="14" alt="add-new-status-icon">
                                </button>
                            </div>

                            <div class="sub-status-container p-12 d-flex flex-column gap-2">
                                <div v-for="status in getStatusesByMainStatus(mainStatus.id)" :key="status.id"
                                    class="bg-white border p-12 rounded" :class="{ 'mb-2': editingStatusId === status.id }">
                                    <div class="d-flex align-items-center gap-2"
                                        :class="{ 'mb-3': editingStatusId === status.id }">
                                        <div class="fs-12 text-gray-900 flex-fill">
                                            <p class="mb-1 fs-14 text-gray-600" v-if="editingStatusId === status.id">Status</p>
                                            <input v-if="editingStatusId === status.id" v-model="editingStatusName"
                                                class="form-control form-control-sm status-input border-gray-300 fw-normal text-gray-600"
                                                @keyup.enter="updateStatus(status.id)" :ref="'editInput_' + status.id">
                                            <span v-else class="status-name">{{ status.name }}</span>
                                        </div>
                                        <div v-if="editingStatusId !== status.id" class="d-flex align-items-center gap-2">
                                            <button
                                                class="btn btn-link p-0 delete-btn mb-0" @click="confirmDeleteStatus(status)"
                                                :disabled="deletingStatusId === status.id">
                                                <img v-if="deletingStatusId !== status.id"
                                                    src="/img/review/icons/trash.svg" class="d-block" width="16"
                                                    alt="delete-status-icon">
                                                <span v-else class="spinner-border spinner-border-sm" role="status"
                                                    aria-hidden="true"></span>
                                            </button>
                                            <button
                                                class="btn btn-link p-0 edit-btn mb-0" @click="editStatus(status)">
                                                <img src="/img/pencil-alt.svg" class="d-block" width="16"
                                                    alt="edit-status-icon">
                                            </button>
                                        </div>
                                    </div>

                                    <!-- Edit Action Buttons -->
                                    <div v-if="editingStatusId === status.id"
                                        class="edit-actions d-flex flex-column gap-2">
                                        <small v-if="isEditStatusTooLong" class="text-danger">
                                            Status name cannot exceed 255 characters.
                                        </small>
                                        <div class="d-flex gap-2 justify-content-end">
                                            <button class="btn btn-sm button-dark bg-gray-50" @click="cancelEdit">
                                                Cancel
                                            </button>
                                            <button class="btn btn-success btn-sm"
                                                :disabled="isEditStatusTooLong || !editingStatusName.trim()"
                                                @click="updateStatus(status.id)">
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="addingStatusFor === mainStatus.id"
                                    class="bg-white border p-12 rounded-lg" :class="{ 'mb-2': addingStatusFor === mainStatus.id }">
                                    <div class="d-flex align-items-center gap-2"
                                        :class="{ 'mb-3': addingStatusFor === mainStatus.id }">
                                        <div class="fs-12 text-gray-900 flex-fill">
                                            <input v-model="newStatusName" class="form-control form-control-sm status-input border-gray-300 fw-normal text-gray-600"
                                                placeholder="Enter status name"
                                                @keyup.enter="!isNewStatusTooLong && createStatus(mainStatus.id)"
                                                :ref="'newStatusInput_' + mainStatus.id">
                                            <small v-if="isNewStatusTooLong" class="text-danger">
                                                Status name cannot exceed 255 characters.
                                            </small>
                                        </div>
                                    </div>

                                    <div class="d-flex align-items-center gap-2 justify-content-end">
                                        <button class="btn btn-sm bg-gray-50 button-dark" @click="cancelAddStatus">
                                            Cancel
                                        </button>
                                        <button class="btn btn-success btn-sm"
                                            :disabled="isNewStatusTooLong || !newStatusName.trim()"
                                            @click="createStatus(mainStatus.id)">
                                            Add
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Delete Confirmation Modal -->
                    <modalDeleteJobStatus ref="deleteModal" :availableStatuses="jobStatuses"
                        @confirmed="deleteStatus" />
                </div>
            </main>
        </div>
    </div>
</template>

<script>
import navigation from "../components/global/navigation.vue";
import modalDeleteJobStatus from "../components/modals/modal-delete-job-status.vue";
import nonPremiumJobScreen from "../components/job-archive/nonPremiumJobScreen.vue";

export default {
    name: 'JobStatus',
    components: {
        navigation,
        modalDeleteJobStatus,
        nonPremiumJobScreen,
    },
    mounted() {
        this.isPremiumAccount =
            _.get(this.$A, "Auth.account.subscriptionState.sku", []).includes(
                "enterprise"
            ) ||
            _.get(this.$A, "Auth.account.subscriptionState.sku", []).includes(
                "premium"
            );

        if (!this.isPremiumAccount) {
            this.isLoading = false;
            return;
        }

        this.loadJobStatuses();
    },
    data() {
        return {
            isLoading: true,
            jobStatuses: [],
            editingStatusId: null,
            editingStatusName: '',
            addingStatusFor: null,
            newStatusName: '',
            deletingStatusId: null,
            isPremiumAccount: false,
            mainStatuses: [
                { id: 1, name: 'Not Started' },
                { id: 2, name: 'To Do' },
                { id: 3, name: 'In Progress' },
                { id: 4, name: 'Done' },
                { id: 5, name: 'Cancelled' }
            ]
        };
    },
    methods: {
        async loadJobStatuses() {
            try {
                this.jobStatuses = await this.$A.JobStatusService.getAllJobStatuses();
                this.isLoading = false;
            } catch (error) {
                console.error('Failed to load job sub statuses:', error);
                this.jobStatuses = [];
            }
        },

        getStatusesByMainStatus(mainStatusId) {
            return this.jobStatuses.filter(status => status.statusCategoryID === mainStatusId);
        },

        getMainStatusIcon(mainStatusId) {
            const icons = {
                1: '/img/backlog.svg',
                2: '/img/todo.svg',
                3: '/img/progress.svg',
                4: '/img/done.svg',
                5: '/img/cancel.svg'
            };
            return icons[mainStatusId] || '/img/block.svg';
        },

        getMainStatusLabel(mainStatusId) {
            const labels = {
                1: 'Not Started',
                2: 'To Do',
                3: 'In Progress',
                4: 'Done',
                5: 'Cancelled'
            };
            return labels[mainStatusId] || 'Unknown';
        },

        addNewStatus(mainStatusId) {
            this.addingStatusFor = mainStatusId;
            this.newStatusName = '';
            this.$nextTick(() => {
                const refName = 'newStatusInput_' + mainStatusId;
                if (this.$refs[refName] && this.$refs[refName][0]) {
                    this.$refs[refName][0].focus();
                }
            });
        },

        cancelAddStatus() {
            this.addingStatusFor = null;
            this.newStatusName = '';
        },

        async createStatus(mainStatusId) {
            if (!this.newStatusName.trim()) {
                this.$toast('Status name is required', {
                    title: 'Validation Error',
                    variant: 'danger',
                });
                return;
            }

            try {
                const currentUser = await this.$A.UserService.GetUser();
                const createRequest = {
                    name: this.newStatusName.trim(),
                    statusCategoryID: mainStatusId,
                    accountId: currentUser.accountId
                };

                await this.$A.JobStatusService.createJobStatus(createRequest);

                this.loadJobStatuses();
                this.cancelAddStatus();
            } catch (error) {
                console.error('Failed to create sub-status:', error);
                this.$toast('Failed to create sub-status', {
                    title: 'Error',
                    variant: 'danger',
                });
            }
        },

        editStatus(status) {
            this.editingStatusId = status.id;
            this.editingStatusName = status.name;
            this.$nextTick(() => {
                const refName = 'editInput_' + status.id;
                if (this.$refs[refName] && this.$refs[refName][0]) {
                    this.$refs[refName][0].focus();
                }
            });
        },

        cancelEdit() {
            this.editingStatusId = null;
            this.editingStatusName = '';
        },

        async updateStatus(StatusId) {
            if (!this.editingStatusName.trim()) {
                this.$toast('Status name is required', {
                    title: 'Validation Error',
                    variant: 'danger',
                });
                return;
            }

            try {
                const status = this.jobStatuses.find(s => s.id === StatusId);
                if (!status) return;

                const updateRequest = {
                    id: StatusId,
                    name: this.editingStatusName.trim(),
                    statusCategoryID: status.statusCategoryID,
                    accountId: status.accountId
                };

                await this.$A.JobStatusService.updateJobStatus(updateRequest);

                this.loadJobStatuses();
                this.cancelEdit();
            } catch (error) {
                console.error('Failed to update sub-status:', error);
                this.$toast('Failed to update sub-status', {
                    title: 'Error',
                    variant: 'danger',
                });
            }
        },

        confirmDeleteStatus(status) {
            this.$refs.deleteModal.open(status);
        },

        async deleteStatus(data) {
            if (!data || !data.status) return;

            this.deletingStatusId = data.status.id;

            try {
                const deleteStatusAndTransferJobs = {
                    currentStatusID: data.status.id,
                    updateStatusID: data.replacementStatusId
                };

                await this.$A.JobStatusService.replaceAndDeleteJobStatus(deleteStatusAndTransferJobs);

                this.loadJobStatuses();
                this.$toast('Job status deleted successfully', {
                    title: 'Success',
                    variant: 'success',
                });
            } catch (error) {
                console.error('Failed to delete sub-status:', error);
                this.$toast('Failed to delete sub-status', {
                    title: 'Error',
                    variant: 'danger',
                });
            } finally {
                this.deletingStatusId = null;
            }
        }
    },
    computed: {
        isNewStatusTooLong() {
            return this.newStatusName.length > 255;
        },
        isEditStatusTooLong() {
            return this.editingStatusName.length > 255;
        }
    }
};
</script>

<style scoped>
.status-input {
    border-radius: 6px !important;
    height: fit-content;
    box-shadow: none;
}

.job-status-subheading {
    border-bottom: 1px solid #F4F4F4 !important;
}

.back-btn a {
    color: #909092;
}

.job-status-carts {
    gap: 18px;
}

.job-status-heading {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.sub-status-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.px-4 {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
}

.lh-1 {
    line-height: 1 !important;
}


.border {
    border: 1px solid #dee2e6 !important;
}

.bg-white {
    background-color: #ffffff !important;
}

.btn {
    display: inline-block;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    user-select: none;
    border: 1px solid transparent;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    border-radius: 0.25rem;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 4px;
}

.mb-10 {
    margin-bottom: 10px !important;
}

.font-weight-bold {
    font-weight: 700 !important;
}

.fs-18 {
    font-size: 18px !important;
}

.border-gray-50 {
    border-color: #f8f9fa !important;
}


@media (max-width: 768px) {
    .action-bar {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.2em;
}

.delete-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.job-status-item {
    border: 1px solid #EBEBED;
    border-radius: 8px;
}

@media (max-width: 992px) {
    .job-details-container {
        padding-left: 16px;
        padding-right: 16px;
    }

    .account-nav {
        justify-content: center;
        background-color: #ffffff;
    }
}
</style>
